import React from 'react'
import format from 'date-fns/format'
import { startCase } from 'lodash'

import { AiSparkles } from '@/components/ai/AiSparkles'
import { Pill } from '@/components/pills/Pill'
import { truncatedColumn, truncatedListColumn } from '@/components/helpers/tableColumnHelpers'
import { formatDuration } from '@/utils/helpers'

import { CallComments } from '@/views/Calls/components/CallComments'
import { TaskNotes } from '@/views/Calls/components/TaskNotes'
import { CHANNEL_TYPE, CHANNELS_WITH_CUSTOMER_ID } from '@/utils/constants'

export const DEFAULT_CALL_COLUMNS = [
  {
    label: 'Agent',
    accessor: 'agent_name',
    isSearchable: true,
    searchSchema: {
      // PJM: update search schema to include the format option code for agents
      type: 'multiSelect',
      label: 'Agents',
      format: (option) => {
        const isAi = option.meta.rowData.is_ai_call

        return (
          <div className="flex">
            {option.label}
            {isAi && <AiSparkles />}
          </div>
        )
      },
    },
    format: (agent, rowData) => {
      const isAi = rowData.is_ai_call

      return (
        <div className="flex">
          {agent}
          {isAi && <AiSparkles />}
        </div>
      )
    },
  },
  {
    label: 'Subject',
    accessor: 'subject',
    isSearchable: true,
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.EMAIL,
    format: (subject) => truncatedColumn(subject),
  },
  {
    label: 'Customer Email',
    accessor: 'customer_email',
    isSearchable: true,
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.EMAIL,
    format: (customerEmail) => truncatedListColumn(customerEmail),
  },
  {
    label: 'VOIP Call ID',
    accessor: 'voip_call_id',
    isSearchable: true,
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
  },
  {
    // ONE entry, not one per channel: column preferences are stored per
    // accessor, so two entries sharing 'voip_customer_id' would share a single
    // show/hide preference across the call and SMS tabs.
    label: 'Customer ID',
    accessor: 'voip_customer_id',
    isSearchable: true,
    hiddenFor: (call_type) => !CHANNELS_WITH_CUSTOMER_ID.includes(call_type),
    // Hide for redacted orgs since all values will be null and filtering won't work
    redactedOrgHidden: true,
  },
  {
    label: 'VOIP Campaign Name',
    accessor: 'voip_campaign_name',
    isSearchable: true,
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
  },
  {
    label: 'Disposition',
    accessor: 'disposition',
    isSearchable: true,
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
  },
  {
    label: 'Outbound Campaign',
    accessor: 'outbound_campaign_name',
    isSearchable: true,
    requiresOutboundCampaigns: true,
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
  },
  {
    label: 'AI Agent Outcome',
    accessor: 'ai_agent_outcome',
    requiresTogo: true,
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
    format: (value) => (value ? startCase(value) : null),
  },
  {
    label: 'AI Agent Disconnection Reason',
    accessor: 'ai_agent_disconnection_reason',
    requiresTogo: true,
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
    format: (value) => (value ? startCase(value) : null),
  },
  {
    label: 'Date Time',
    accessor: 'start_time',
    format: (dateInSeconds) => format(new Date(dateInSeconds || 0), "MMM d',' yyyy h:mm aa"),
  },
  {
    label: 'Playbook',
    accessor: 'playbook_name',
    isSearchable: true,
    searchSchema: {
      type: 'multiSelect',
      label: 'Playbooks',
    },
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
    format: (playbook) => truncatedColumn(playbook),
  },
  {
    label: 'Duration',
    accessor: 'duration',
    format: (duration) => formatDuration(duration),
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
  },
  {
    label: 'Win',
    accessor: 'is_win',
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
    audioIngestionOnlyHidden: true,
    format: (isWin) => {
      // for postcall only calls will be null, don't show any win marker
      if (isWin == null) return null

      return (
        <div className="win-column-entry">
          <Pill small success={isWin} critical={!isWin}>
            {isWin ? 'Win' : 'Non-Win'}
          </Pill>
        </div>
      )
    },
  },
  {
    label: 'Attachments',
    accessor: 'attachments',
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
  },
  {
    label: 'Direction',
    accessor: 'direction',
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
  },
  {
    label: 'Message Count',
    accessor: 'message_count',
    hiddenFor: (call_type) => call_type === CHANNEL_TYPE.CALL,
  },
  {
    label: 'Customer Ending Sentiment',
    accessor: 'ending_sentiment',
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
    audioIngestionOnlyHidden: true,
  },
  {
    label: 'Notes',
    accessor: 'call_summary',
    isParent: true,
    pillLabel: 'AI',
  },
  {
    label: 'Extracted Data',
    accessor: 'extracted_data',
    isParent: true,
    pillLabel: 'AI',
  },
  {
    label: 'Task Notes',
    accessor: 'task_notes',
    format: (tasks = [], rowData) =>
      tasks.length > 0 ? (
        <TaskNotes
          tasks={tasks}
          callId={rowData.call_id}
          recordType={rowData.record_type}
          messageId={rowData.message_id}
        />
      ) : null,
  },
  {
    label: 'Comments',
    accessor: 'coaching_comments',
    hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
    format: (comments = [], rowData) => (
      <CallComments
        comments={comments}
        callId={rowData.call_id}
        recordType={rowData.record_type}
        messageId={rowData.message_id}
      />
    ),
  },
  { label: 'Scores', accessor: 'scores', isParent: true },
  // format prevents raw array rendering before tag category child columns load
  { label: 'Tags', accessor: 'tags', isParent: true, format: () => null },
]
